import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import authService, { type UserProfile } from '../services/authService';
import { workspaceService, type OrganizerContext, type WorkspaceContext } from '../services/workspaceService';

interface AdminAuthContextType {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  isPlatformAdmin: boolean;
  canAccessAdmin: boolean;
  canCreateEvents: boolean;
  canManageWorkspace: boolean;
  organizerContext: OrganizerContext | null;
  activeWorkspace: WorkspaceContext | null;
  activeWorkspaceId: string | null;
  setActiveWorkspaceId: (workspaceId: string | null) => void;
  refreshOrganizerContext: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const fallbackContext: AdminAuthContextType = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isAdmin: false,
  isPlatformAdmin: false,
  canAccessAdmin: true,
  canCreateEvents: true,
  canManageWorkspace: true,
  organizerContext: null,
  activeWorkspace: null,
  activeWorkspaceId: null,
  setActiveWorkspaceId: () => undefined,
  refreshOrganizerContext: async () => undefined,
  signIn: async () => ({ error: new Error('Admin authentication is unavailable') }),
  signOut: async () => undefined,
};

const AdminAuthContext = createContext<AdminAuthContextType>(fallbackContext);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [organizerContext, setOrganizerContext] = useState<OrganizerContext | null>(null);
  const [activeWorkspaceId, setActiveWorkspace] = useState<string | null>(null);

  const selectWorkspace = (context: OrganizerContext | null) => {
    const stored = localStorage.getItem('active_workspace_id');
    const storedIsValid = context?.workspaces.some((workspace) => workspace.id === stored);
    const next = (storedIsValid ? stored : null)
      ?? context?.workspaces.find((workspace) => workspace.status === 'approved')?.id
      ?? context?.workspaces[0]?.id
      ?? null;
    setActiveWorkspace(next);
    if (next) localStorage.setItem('active_workspace_id', next);
    else localStorage.removeItem('active_workspace_id');
  };

  const refreshOrganizerContext = async () => {
    try {
      const context = await workspaceService.getContext();
      setOrganizerContext(context);
      selectWorkspace(context);
    } catch {
      setOrganizerContext(null);
      selectWorkspace(null);
    }
  };

  useEffect(() => {
    async function checkSession() {
      const session = await authService.getSession();
      if (session) {
        const profile = await authService.getCurrentUser();
        setUser(profile);
        if (profile) await refreshOrganizerContext();
      }
      setIsLoading(false);
    }
    void checkSession();
    const handler = () => {
      setUser(null);
      setOrganizerContext(null);
      selectWorkspace(null);
    };
    window.addEventListener('auth:expired', handler);
    return () => window.removeEventListener('auth:expired', handler);
  }, []);

  const signIn = async (email: string, password: string): Promise<{ error: Error | null }> => {
    try {
      await authService.login(email, password);
      const profile = await authService.getCurrentUser();
      if (!profile) return { error: new Error('Unable to load your account') };
      setUser(profile);
      await refreshOrganizerContext();
      return { error: null };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('Login failed') };
    }
  };

  const signOut = async () => {
    await authService.logout();
    setUser(null);
    setOrganizerContext(null);
    selectWorkspace(null);
  };

  const setActiveWorkspaceId = (workspaceId: string | null) => {
    const valid = organizerContext?.workspaces.some((workspace) => workspace.id === workspaceId);
    const next = valid ? workspaceId : null;
    setActiveWorkspace(next);
    if (next) localStorage.setItem('active_workspace_id', next);
    else localStorage.removeItem('active_workspace_id');
  };

  const isAuthenticated = !!user;
  const isPlatformAdmin = !!organizerContext?.platform_admin;
  const canAccessAdmin = !!organizerContext?.can_access_admin;
  const activeWorkspace = organizerContext?.workspaces.find(
    (workspace) => workspace.id === activeWorkspaceId,
  ) ?? null;
  const canCreateEvents = !!activeWorkspace?.can_create_events;
  const canManageWorkspace = !!activeWorkspace?.can_manage_workspace;

  return (
    <AdminAuthContext.Provider value={{
      user,
      isAuthenticated,
      isLoading,
      isAdmin: isPlatformAdmin || canAccessAdmin,
      isPlatformAdmin,
      canAccessAdmin,
      canCreateEvents,
      canManageWorkspace,
      organizerContext,
      activeWorkspace,
      activeWorkspaceId,
      setActiveWorkspaceId,
      refreshOrganizerContext,
      signIn,
      signOut,
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  return useContext(AdminAuthContext);
}
